import React, { Component } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import { isDefined } from "../../utils";
import {
  formatPrice,
  normalizePrice,
  computeReaisDistance,
} from "../../../../utils/priceUtils";
import {
  getOrderSideText,
  ORDER_SIDE_COLORS,
  ORDER_UI_COLORS,
} from "../../../../utils/orderStyles";
import { hexToRGBA } from "../../utils";
import { useOrderDefaultsStore } from "../../../../hooks/useOrderDefaults";
import { buildOtoLegs } from "../../../../utils/otoLegs";
import { formatNumber } from "../../../../utils/numberFormatting";

// Styles for the blocked key toast
const toastStyles = {
  position: "fixed",
  top: "80px",
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: "rgba(30, 30, 30, 0.95)",
  color: "rgba(255, 255, 255, 0.95)",
  padding: "10px 20px",
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: "600",
  fontFamily:
    "SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  zIndex: 10000,
  pointerEvents: "none",
  whiteSpace: "nowrap",
};

const LABEL_FONT =
  "700 11px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
const LABEL_HEIGHT = 20;
const LABEL_EDGE_OFFSET = 8;
// OTO preview colours — ganho (green) / perda (red), matching the DoM preview.
const OTO_PROFIT_COLOR = "#16a34a";
const OTO_LOSS_COLOR = "#dc2626";

/**
 * OrderHoverIndicator - Shows a horizontal line with price label when modifier keys are pressed
 * and one-click trading is enabled. Similar to local order interactive but for hover state.
 *
 * When OTO is armed for the symbol, the would-be ganho (take-profit) and perda (stop-loss)
 * levels are previewed as extra dashed lines at their real prices — the same legs that
 * buildOtoLegs would attach to the order on click.
 */
class OrderHoverIndicator extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleUnHover = this.handleUnHover.bind(this);

    this.mutableState = {
      price: null,
      y: null,
      isActive: false,
      side: null,
      oto: null,
    };
  }

  handleMouseMove(moreProps, e) {
    const {
      enabled,
      oneClickTrading,
      symbol,
      shiftArmed,
      ctrlArmed,
      metaArmed,
      modifierKeyDisarming,
    } = this.props;

    if (!enabled || !oneClickTrading) {
      this.mutableState.isActive = false;
      return;
    }

    // Check for modifier keys
    const { shiftKey, ctrlKey, metaKey } = e;

    // Check if the pressed modifier key is armed (released since last order)
    // Only show hover indicator if the key can actually create an order
    // If modifierKeyDisarming is disabled (false), always consider keys as armed
    const isShiftActive =
      shiftKey && (modifierKeyDisarming ? shiftArmed : true);
    const isCtrlActive = ctrlKey && (modifierKeyDisarming ? ctrlArmed : true);
    const isMetaActive = metaKey && (modifierKeyDisarming ? metaArmed : true);
    const hasArmedModifierKey = isShiftActive || isCtrlActive || isMetaActive;

    if (hasArmedModifierKey) {
      const {
        mouseXY,
        chartConfig: { yScale },
      } = moreProps;

      if (mouseXY && yScale) {
        const mouseY = mouseXY[1];
        const rawPrice = yScale.invert(mouseY);

        // Normalize price to respect min step (tick size) for the symbol
        const normalizedPrice = normalizePrice(symbol, rawPrice);

        // Recalculate Y position for the normalized price to ensure visual alignment
        const normalizedY = yScale(normalizedPrice);

        const side = shiftKey ? "buy" : "sell"; // Shift = buy, Ctrl/Cmd = sell

        this.mutableState = {
          price: normalizedPrice,
          y: normalizedY,
          isActive: true,
          side,
          oto: this.computeOtoPreview(symbol, side, normalizedPrice, yScale),
        };
      }
    } else {
      this.mutableState.isActive = false;
    }
  }

  // Clear the preview when the pointer leaves the chart (#361). handleMouseMove
  // only deactivates on a modifier-less mousemove, but once the cursor exits the
  // chart no such event arrives — so without this the last frame stays painted on
  // the mouse canvas (the line "sticks" on a chart that lost focus, e.g. when the
  // pointer moves to a side-by-side chart). ChartCanvas.handleMouseLeave drives
  // this via GenericChartComponent's onUnHover, then clears + redraws the mouse
  // canvas; with isActive false, drawOnCanvas paints nothing.
  handleUnHover() {
    this.mutableState.isActive = false;
  }

  // Project the OTO ganho/perda legs of the would-be order. Reads the live OTO
  // config from the store (same source the click handler uses), so the preview
  // tracks toggles made just before hovering. Returns null when OTO is off.
  computeOtoPreview(symbol, side, anchorPrice, yScale) {
    const store = useOrderDefaultsStore.getState();
    const otoConfig = store.getOtoConfig(symbol);
    if (!otoConfig.enabled) return null;

    const quantity = store.getAdjustedQuantity(symbol);
    const legs = buildOtoLegs({
      mainOrderId: "hover",
      symbol,
      side,
      quantity,
      anchorPrice,
      lossTicks: otoConfig.lossTicks,
      profitTicks: otoConfig.profitTicks,
    });
    if (legs.length < 2) return null;

    const stop = legs.find((l) => l.type === "stop");
    const profit = legs.find((l) => l.type === "profit");
    if (!stop || !profit) return null;

    const legState = (leg, ticks) => ({
      price: leg.price,
      y: yScale(leg.price),
      ticks,
      reais: computeReaisDistance(symbol, anchorPrice, leg.price, quantity),
    });

    return {
      profit: legState(profit, otoConfig.profitTicks),
      loss: legState(stop, otoConfig.lossTicks),
    };
  }

  // Compose a leg label: "GANHO: 5.822 · 10t · R$ 50,00".
  otoLabel(kind, leg) {
    const { symbol } = this.props;
    const parts = [
      `${kind}: ${formatPrice(symbol, leg.price)}`,
      `${leg.ticks}t`,
    ];
    if (leg.reais !== undefined)
      parts.push(`R$ ${formatNumber(Math.abs(leg.reais), 2)}`);
    return parts.join("  ·  ");
  }

  // The set of horizontal levels to render — entry plus OTO legs when armed.
  // Y is recomputed from price against the live yScale so the lines stay
  // aligned through pans/zooms between mouse moves.
  buildLevels(moreProps) {
    const { isActive, price, y, side, oto } = this.mutableState;
    if (!isActive || !isDefined(price) || !isDefined(y)) return null;

    // The preview belongs to this chart panel. When the pointer is over another
    // panel of the same canvas (volume/indicator), handleMouseMove is gated by
    // currentCharts and never deactivates — so without this guard every redraw
    // repainted the stale frame ("sombra agarrada", #362). currentCharts is
    // kept fresh by updateMoreProps even while the handler is gated.
    const { chartId, currentCharts } = moreProps || {};
    if (
      Array.isArray(currentCharts) &&
      isDefined(chartId) &&
      currentCharts.indexOf(chartId) === -1
    )
      return null;

    const { symbol } = this.props;
    const yScale = moreProps?.chartConfig?.yScale;
    const at = (p, fallback) => (yScale ? yScale(p) : fallback);

    const levels = [
      {
        y: at(price, y),
        color: ORDER_SIDE_COLORS[side] || ORDER_SIDE_COLORS.default,
        text: `${getOrderSideText(side)}: ${formatPrice(symbol, price)}`,
      },
    ];

    if (oto) {
      levels.push({
        y: at(oto.profit.price, oto.profit.y),
        color: OTO_PROFIT_COLOR,
        text: this.otoLabel("GANHO", oto.profit),
      });
      levels.push({
        y: at(oto.loss.price, oto.loss.y),
        color: OTO_LOSS_COLOR,
        text: this.otoLabel("PERDA", oto.loss),
      });
    }

    return levels;
  }

  drawOnCanvas(ctx, moreProps) {
    const levels = this.buildLevels(moreProps);
    if (!levels) return;

    const { width } = moreProps;
    ctx.save();
    levels.forEach((level) => this.drawLevel(ctx, width, level));
    ctx.restore();
  }

  // One horizontal dashed line + right-aligned price label.
  drawLevel(ctx, width, { y, color, text }) {
    // Dashed line
    ctx.globalAlpha = 0.8;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();

    // Label box
    ctx.font = LABEL_FONT;
    const labelWidth = ctx.measureText(text).width + 16; // 8px padding each side
    const labelX = width - labelWidth - LABEL_EDGE_OFFSET;
    const labelY = y - LABEL_HEIGHT / 2;

    ctx.fillStyle = hexToRGBA(color, 0.95);
    ctx.strokeStyle = "rgba(255,255,255,0.1)";
    ctx.lineWidth = 0.5;
    ctx.setLineDash([]);
    this.roundRect(ctx, labelX, labelY, labelWidth, LABEL_HEIGHT, 3);
    ctx.fill();
    ctx.stroke();

    // Label text
    ctx.fillStyle = ORDER_UI_COLORS.textPrimary;
    ctx.font = LABEL_FONT;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.globalAlpha = 1.0;
    ctx.fillText(text, labelX + labelWidth / 2, labelY + LABEL_HEIGHT / 2);
  }

  // Helper method for rounded rectangles (same as used in order components)
  roundRect(ctx, x, y, width, height, radius) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  renderSVG(moreProps) {
    const levels = this.buildLevels(moreProps);
    if (!levels) return null;

    const { width } = moreProps;

    return (
      <g className="react-stockcharts-order-hover-indicator">
        {levels.map((level, i) => {
          const labelWidth = level.text.length * 7 + 16; // Approximate width + padding
          const labelX = width - labelWidth - LABEL_EDGE_OFFSET;
          const labelY = level.y - LABEL_HEIGHT / 2;
          return (
            <g key={i}>
              <line
                x1={0}
                y1={level.y}
                x2={width}
                y2={level.y}
                stroke={level.color}
                strokeWidth={2}
                strokeDasharray="5,5"
                opacity={0.8}
              />
              <rect
                x={labelX}
                y={labelY}
                width={labelWidth}
                height={LABEL_HEIGHT}
                fill={hexToRGBA(level.color, 0.95)}
                stroke="rgba(255,255,255,0.1)"
                strokeWidth={0.5}
                rx={3}
              />
              <text
                x={labelX + labelWidth / 2}
                y={level.y}
                fill={ORDER_UI_COLORS.textPrimary}
                fontSize="11"
                fontFamily="SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif"
                fontWeight="700"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {level.text}
              </text>
            </g>
          );
        })}
      </g>
    );
  }

  renderBlockedToast() {
    const { blockedKey } = this.props;

    if (!blockedKey) return null;

    const message = `Solte a tecla ${blockedKey} e pressione novamente para colocar mais ordens.`;

    // PoC #popout: show the toast in the window the chart actually lives in.
    const hostDoc = this._anchorNode?.ownerDocument ?? document;
    return ReactDOM.createPortal(
      <div style={toastStyles}>{message}</div>,
      hostDoc.body,
    );
  }

  render() {
    const { enabled } = this.props;

    if (!enabled) return null;

    return (
      <>
        <g
          ref={(el) => {
            this._anchorNode = el;
          }}
          style={{ display: "none" }}
        />
        <GenericChartComponent
          clip={true}
          svgDraw={this.renderSVG}
          canvasDraw={this.drawOnCanvas}
          canvasToDraw={getMouseCanvas}
          onMouseMove={this.handleMouseMove}
          onUnHover={this.handleUnHover}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
        />
        {this.renderBlockedToast()}
      </>
    );
  }
}

OrderHoverIndicator.propTypes = {
  enabled: PropTypes.bool.isRequired,
  oneClickTrading: PropTypes.bool.isRequired,
  symbol: PropTypes.string.isRequired,
  shiftArmed: PropTypes.bool,
  ctrlArmed: PropTypes.bool,
  metaArmed: PropTypes.bool,
  modifierKeyDisarming: PropTypes.bool, // If true, require key release between orders
  blockedKey: PropTypes.string, // 'Shift', 'Ctrl', or 'Cmd' when click was blocked
};

OrderHoverIndicator.defaultProps = {
  enabled: true,
  oneClickTrading: false,
  symbol: "",
  shiftArmed: true,
  ctrlArmed: true,
  metaArmed: true,
  modifierKeyDisarming: false, // Default: no restriction
  blockedKey: null,
};

export default OrderHoverIndicator;
